/**
 * Inventory React Query Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { inventoryItemKeys } from './query-keys';
import * as api from '../api/inventory';
import type {
  InventoryItemFilters,
  CreateInventoryItemDto,
  UpdateInventoryItemDto,
  AdjustStockDto,
  ReserveStockDto,
} from '../types/inventory';

/**
 * Get inventory KPIs
 */
export const useInventoryKpis = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: inventoryItemKeys.kpis(),
    queryFn: api.getInventoryKpis,
    enabled: options?.enabled ?? true,
  });
};

/**
 * Get inventory items with filters
 */
export const useInventoryItems = (params?: InventoryItemFilters) => {
  return useQuery({
    queryKey: inventoryItemKeys.list(params),
    queryFn: () => api.getInventoryItems(params),
  });
};

/**
 * Get inventory item by ID
 */
export const useInventoryItem = (id: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: inventoryItemKeys.detail(id),
    queryFn: () => api.getInventoryItem(id),
    enabled: !!id && (options?.enabled ?? true),
  });
};

/**
 * Get inventory item by product ID
 */
export const useInventoryItemByProduct = (productId: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: inventoryItemKeys.byProduct(productId),
    queryFn: () => api.getInventoryItemByProduct(productId),
    enabled: !!productId && (options?.enabled ?? true),
  });
};

/**
 * Create inventory item mutation
 */
export const useCreateInventoryItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateInventoryItemDto) => api.createInventoryItem(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inventoryItemKeys.all });
    },
  });
};

/**
 * Update inventory item mutation
 */
export const useUpdateInventoryItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateInventoryItemDto }) =>
      api.updateInventoryItem(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: inventoryItemKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: inventoryItemKeys.lists() });
      queryClient.invalidateQueries({ queryKey: inventoryItemKeys.kpis() });
    },
  });
};

/**
 * Delete inventory item mutation
 */
export const useDeleteInventoryItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.deleteInventoryItem(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inventoryItemKeys.all });
    },
  });
};

/**
 * Adjust stock mutation
 */
export const useAdjustStock = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: AdjustStockDto }) =>
      api.adjustStock(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: inventoryItemKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: inventoryItemKeys.lists() });
      queryClient.invalidateQueries({ queryKey: inventoryItemKeys.kpis() });
    },
  });
};

/**
 * Reserve stock mutation
 */
export const useReserveStock = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ReserveStockDto }) =>
      api.reserveStock(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: inventoryItemKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: inventoryItemKeys.lists() });
      queryClient.invalidateQueries({ queryKey: inventoryItemKeys.kpis() });
    },
  });
};

/**
 * Release reservation mutation
 */
export const useReleaseReservation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ReserveStockDto }) =>
      api.releaseReservation(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: inventoryItemKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: inventoryItemKeys.lists() });
      queryClient.invalidateQueries({ queryKey: inventoryItemKeys.kpis() });
    },
  });
};

/**
 * Check availability query
 */
export const useCheckAvailability = (
  id: string,
  quantity: number,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: inventoryItemKeys.availability(id, quantity),
    queryFn: () => api.checkAvailability(id, quantity),
    enabled: !!id && quantity > 0 && (options?.enabled ?? true),
  });
};
